<template>
  <div>
    <div class="modal fade show" tabindex="-1" role="dialog" style="display: block; background: rgba(0,0,0,0.5);" v-if="show">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{$t('subscription_details')}}</h5>
            <button type="button" class="btn-close" @click="$emit('close')"></button>
          </div>
          <div class="modal-body">
            <div v-if="subscription" class="subscription-details-container">
              <!-- Top Section: Membership Card -->
              <div class="row mb-4">
                <div class="col-12">
                  <div class="subscription-details modal-view" ref="subscriptionDetails">
                    <div class="business-card">
                      <div class="card-front">
                        <!-- Header with Logo -->
                        <div class="card-header">
                          <img :src="business.logo" alt="Business Logo" class="business-logo" />
                          <div class="company-info">
                            <h3 class="company-name">{{business.name}}</h3>
                            <p class="company-tagline">Premium Membership Card</p>
                          </div>
                        </div>

                        <!-- Main Content -->
                        <div class="card-body">
                          <div class="client-section">
                            <div class="client-info">
                              <h2 class="client-name">{{ subscription?.client?.name }}</h2>
                              <div class="membership-info">
                                <div class="info-item">
                                  <span class="info-label">{{ $t('member_since') }}</span>
                                  <span class="info-value">{{ formatDate(subscription.start_date) }}</span>
                                </div>
                                <div class="info-item">
                                  <span class="info-label">Member ID</span>
                                  <span class="info-value">#{{ subscription?.client?.id }}</span>
                                </div>
                              </div>
                            </div>
                            
                            <div class="qr-section">
                              <div class="qr-container">
                                <img v-if="qrCodeUrl && qrCodeUrl !== 'NO_CLIENT_ID' && qrCodeUrl !== 'ERROR'" :src="qrCodeUrl" alt="Membership QR Code" class="qr-code" />
                                <div v-else-if="qrCodeUrl === 'NO_CLIENT_ID'" class="qr-loading">
                                  <AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle text-warning" style="font-size: 2rem;" />
                                  <small class="text-muted mt-2">No client ID available</small>
                                </div>
                                <div v-else-if="qrCodeUrl === 'ERROR'" class="qr-loading">
                                  <AppIcon name="bi-x-circle" class="bi bi-x-circle text-danger" style="font-size: 2rem;" />
                                  <small class="text-muted mt-2">Error generating QR code</small>
                                </div>
                                <div v-else class="qr-loading">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                  <small class="text-muted mt-2">Generating...</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Footer -->
                        <div class="card-footer">
                          <div class="contact-info">
                            <span class="contact-item">
                              <AppIcon name="bi-envelope" class="bi bi-envelope" />
                              {{business.email}}
                            </span>
                            <span class="contact-item">
                              <AppIcon name="bi-phone" class="bi bi-phone" />
                              {{business.phone}}
                            </span>
                            <span class="contact-item">
                              <AppIcon name="bi-globe" class="bi bi-globe" />
                              {{business.website}}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Subscription Information Cards -->
              <div class="row g-3 mb-4">
                <!-- Status Card -->
                <div class="col-md-6 col-lg-4">
                  <div class="info-card">
                    <div class="info-card-icon" :class="statusColorClass">
                      <AppIcon name="bi-circle-fill" class="bi bi-circle-fill" />
                    </div>
                    <div class="info-card-content">
                      <h6 class="info-card-title">{{ $t('status') }}</h6>
                      <p class="info-card-value" :class="statusColorClass">{{ $t(subscription.status) }}</p>
                    </div>
                  </div>
                </div>

                <!-- Plan Card -->
                <div class="col-md-6 col-lg-4">
                  <div class="info-card">
                    <div class="info-card-icon text-primary">
                      <AppIcon name="bi-card-list" class="bi bi-card-list" />
                    </div>
                    <div class="info-card-content">
                      <h6 class="info-card-title">{{ $t('plan') }}</h6>
                      <p class="info-card-value">{{ subscription.plan?.name || 'N/A' }}</p>
                      <small class="text-muted">{{ subscription.plan?.price }} {{ business.currency }}</small>
                    </div>
                  </div>
                </div>

                <!-- Duration Card -->
                <div class="col-md-6 col-lg-4">
                  <div class="info-card">
                    <div class="info-card-icon text-info">
                      <AppIcon name="bi-calendar-range" class="bi bi-calendar-range" />
                    </div>
                    <div class="info-card-content">
                      <h6 class="info-card-title">{{ $t('duration') }}</h6>
                      <p class="info-card-value">{{ calculateDaysRemaining() }} {{ $t('days') }}</p>
                      <small class="text-muted">{{ $t('remaining') }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Detailed Information Tabs -->
              <div class="details-section">
                <ul class="nav nav-tabs mb-3" role="tablist">
                  <li class="nav-item">
                    <a class="nav-link active" data-bs-toggle="tab" href="#details" role="tab">
                      <AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />{{ $t('details') }}
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" data-bs-toggle="tab" href="#attendance" role="tab">
                      <AppIcon name="bi-calendar-check" class="bi bi-calendar-check me-2" />{{ $t('attendance') }}
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" data-bs-toggle="tab" href="#history" role="tab">
                      <AppIcon name="bi-clock-history" class="bi bi-clock-history me-2" />{{ $t('history') }}
                    </a>
                  </li>
                </ul>

                <div class="tab-content">
                  <!-- Details Tab -->
                  <div class="tab-pane fade show active" id="details" role="tabpanel">
                    <div class="row g-3">
                      <!-- Subscription Dates -->
                      <div class="col-12">
                        <h6 class="mb-3 text-primary">
                          <AppIcon name="bi-calendar-range" class="bi bi-calendar-range me-2" />{{ $t('subscription_period') }}
                        </h6>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>{{ $t('start_date') }}:</strong>
                          <span>{{ formatDate(subscription.start_date) }}</span>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>{{ $t('end_date') }}:</strong>
                          <span>{{ formatDate(subscription.end_date) }}</span>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>{{ $t('created_at') }}:</strong>
                          <span>{{ formatDateTime(subscription.created_at) }}</span>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>{{ $t('last_updated') }}:</strong>
                          <span>{{ formatDateTime(subscription.updated_at) }}</span>
                        </div>
                      </div>

                      <!-- Client Information -->
                      <div class="col-12 mt-4">
                        <h6 class="mb-3 text-primary">
                          <AppIcon name="bi-person" class="bi bi-person me-2" />{{ $t('client_information') }}
                        </h6>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>{{ $t('name') }}:</strong>
                          <span>{{ subscription.client?.name || 'N/A' }}</span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="subscription.client?.email">
                        <div class="detail-item">
                          <strong>{{ $t('email') }}:</strong>
                          <span>{{ subscription.client.email }}</span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="subscription.client?.phone">
                        <div class="detail-item">
                          <strong>{{ $t('phone') }}:</strong>
                          <span>{{ subscription.client.phone }}</span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="subscription.client?.address">
                        <div class="detail-item">
                          <strong>{{ $t('address') }}:</strong>
                          <span>{{ subscription.client.address }}</span>
                        </div>
                      </div>

                      <!-- Payment Information -->
                      <div class="col-12 mt-4">
                        <h6 class="mb-3 text-primary">
                          <AppIcon name="bi-credit-card" class="bi bi-credit-card me-2" />{{ $t('payment_information') }}
                        </h6>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>{{ $t('price') }}:</strong>
                          <span class="text-success fw-bold">{{ subscription.plan?.price }} {{ business.currency }}</span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="subscription.payment_status">
                        <div class="detail-item">
                          <strong>{{ $t('payment_status') }}:</strong>
                          <span :class="subscription.payment_status === 'paid' ? 'badge bg-success' : 'badge bg-warning'">
                            {{ $t(subscription.payment_status) }}
                          </span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="subscription.payment_method">
                        <div class="detail-item">
                          <strong>{{ $t('payment_method') }}:</strong>
                          <span>{{ subscription.payment_method }}</span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="subscription.transaction_id">
                        <div class="detail-item">
                          <strong>{{ $t('transaction_id') }}:</strong>
                          <span class="text-muted">{{ subscription.transaction_id }}</span>
                        </div>
                      </div>

                      <!-- Additional Notes -->
                      <div class="col-12 mt-4" v-if="subscription.notes">
                        <h6 class="mb-3 text-primary">
                          <AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{ $t('notes') }}
                        </h6>
                        <div class="detail-item">
                          <p class="mb-0 bg-light p-3 rounded">{{ subscription.notes }}</p>
                        </div>
                      </div>
                      
                      <!-- Plan Features -->
                      <div class="col-12 mt-4" v-if="subscription.plan">
                        <h6 class="mb-3 text-primary">
                          <AppIcon name="bi-list-check" class="bi bi-list-check me-2" />{{ $t('plan_features') }}
                        </h6>
                        <div class="plan-features">
                          <div class="feature-item" v-if="subscription.plan.max_visits_per_day">
                            <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" />
                            <span>{{ subscription.plan.max_visits_per_day }} {{ $t('visits_per_day') }}</span>
                          </div>
                          <div class="feature-item" v-if="subscription.plan.max_visits_per_month">
                            <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" />
                            <span>{{ subscription.plan.max_visits_per_month }} {{ $t('visits_per_month') }}</span>
                          </div>
                          <div class="feature-item" v-if="subscription.plan.access_hours_start && subscription.plan.access_hours_end">
                            <AppIcon name="bi-clock" class="bi bi-clock text-info" />
                            <span>{{ $t('access_hours') }}: {{ subscription.plan.access_hours_start }} - {{ subscription.plan.access_hours_end }}</span>
                          </div>
                          <div class="feature-item" v-if="subscription.plan.allows_freeze">
                            <AppIcon name="bi-snowflake" class="bi bi-snowflake text-primary" />
                            <span>{{ $t('freeze_allowed') }} ({{ subscription.plan.max_freeze_days }} {{ $t('days') }})</span>
                          </div>
                          <div class="feature-item" v-if="subscription.plan.guest_passes">
                            <AppIcon name="bi-person-plus" class="bi bi-person-plus text-warning" />
                            <span>{{ subscription.plan.guest_passes }} {{ $t('guest_passes') }}</span>
                          </div>
                          <div class="feature-item" v-if="subscription.plan.description">
                            <AppIcon name="bi-info-circle" class="bi bi-info-circle text-secondary" />
                            <span>{{ subscription.plan.description }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Attendance Tab -->
                  <div class="tab-pane fade" id="attendance" role="tabpanel">
                    <table-loading-bar v-if="loadingAttendance" class="text-center py-4" />
                    <div v-if="!loadingAttendance" class="table-responsive">
                      <table class="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>{{ $t('date') }}</th>
                            <th>{{ $t('check_in') }}</th>
                            <th>{{ $t('check_out') }}</th>
                            <th>{{ $t('duration') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="record in attendanceRecords" :key="record.id">
                            <td>{{ formatDate(record.check_in) }}</td>
                            <td>{{ formatTime(record.check_in) }}</td>
                            <td>{{ record.check_out ? formatTime(record.check_out) : '-' }}</td>
                            <td>{{ calculateDuration(record.check_in, record.check_out) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-if="!loadingAttendance && attendanceRecords.length === 0" class="text-center py-4 text-muted">
                      <AppIcon name="bi-calendar-x" class="bi bi-calendar-x" style="font-size: 3rem;" />
                      <p class="mt-2">{{ $t('no_attendance_records') }}</p>
                    </div>
                  </div>

                  <!-- History Tab -->
                  <div class="tab-pane fade" id="history" role="tabpanel">
                    <document-history :history="historyRecords" :loading="loadingHistory" />
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Hidden element for clean capture -->
            <div v-if="subscription" class="subscription-details capture-only" ref="captureElement" style="position: absolute; left: -9999px; top: -9999px;">
              <div class="business-card">
                <div class="card-front">
                  <!-- Header with Logo -->
                  <div class="card-header">
                    <img :src="business.logo" alt="Business Logo" class="business-logo" />
                    <div class="company-info">
                      <h3 class="company-name">{{business.name}}</h3>
                      <p class="company-tagline">Premium Membership Card</p>
                    </div>
                  </div>

                  <!-- Main Content -->
                  <div class="card-body">
                    <div class="client-section">
                      <div class="client-info">
                        <h2 class="client-name">{{ subscription?.client?.name }}</h2>
                        <div class="membership-info">
                          <div class="info-item">
                            <span class="info-label">{{ $t('member_since') }}</span>
                            <span class="info-value">{{ formatDate(subscription.start_date) }}</span>
                          </div>
                          <div class="info-item">
                            <span class="info-label">Member ID</span>
                            <span class="info-value">#{{ subscription?.client?.id }}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div class="qr-section">
                        <div class="qr-container">
                          <img :src="qrCodeUrl" alt="Membership QR Code" class="qr-code" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Footer -->
                  <div class="card-footer">
                    <div class="contact-info">
                      <span class="contact-item">
                        <AppIcon name="bi-envelope" class="bi bi-envelope" />
                        {{business.email}}
                      </span>
                      <span class="contact-item">
                        <AppIcon name="bi-phone" class="bi bi-phone" />
                        {{business.phone}}
                      </span>
                      <span class="contact-item">
                        <AppIcon name="bi-globe" class="bi bi-globe" />
                        {{business.website}}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="d-flex gap-2 flex-wrap justify-content-end">
              <button type="button" class="btn btn-sm btn-primary" @click="downloadSubscriptionDetails">
                <AppIcon name="bi-download" class="bi bi-download me-1" />
                {{ $t('download') }}
              </button>
              <button type="button" class="btn btn-sm btn-secondary" @click="$emit('close')">
                <AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />
                {{$t('close')}}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import helper from '../helper';
import DocumentHistory from './DocumentHistory.vue';

export default {
  name: 'ShowSubscription',
  components: { DocumentHistory },
  props: {
    show: Boolean,
    subscription: Object
  },
  data() {
    return {
      qrCodeUrl: '',
      showEmailInput: false,
      emailAddress: '',
      sendingEmail: false,
      attendanceRecords: [],
      historyRecords: [],
      loadingAttendance: false,
      loadingHistory: false
    }
  },
  computed: {
    business(){ return this.$store.getters.BUSINESS},
    statusColorClass() {
      if (!this.subscription) return '';
      const status = this.subscription.status;
      return {
        'text-success': status === 'active',
        'text-danger': status === 'canceled',
        'text-secondary': status === 'expired'
      };
    }
  },
  watch: {
    subscription: {
      immediate: true,
      async handler(newValue) {
        if (newValue && this.show) {
          await this.generateQRCode();
        }
      }
    },
    show: {
      immediate: true,
      async handler(newValue) {
        if (newValue && this.subscription) {
          await this.generateQRCode();
          
          // Fetch additional data
          this.fetchAttendanceRecords();
          this.fetchHistoryRecords();
          if (this.subscription.client?.email) {
            this.emailAddress = this.subscription.client.email;
          }
        }
      }
    }
  },
  methods: {
    async generateQRCode() {
      // Don't reset if we have no subscription
      if (!this.subscription) {
        return;
      }
      
      // Try different ways to get client ID
      let clientId = null;
      
      if (this.subscription?.client?.id) {
        clientId = this.subscription.client.id;
      } else if (this.subscription?.client_id) {
        clientId = this.subscription.client_id;
      }
      
      if (!clientId) {
        alert('No client ID found. Client object:', this.subscription?.client, 'Client ID field:', this.subscription?.client_id);
        this.qrCodeUrl = 'NO_CLIENT_ID'; // Special value to show error state
        return;
      }
      
      // Now reset and generate
      this.qrCodeUrl = '';
      await this.$nextTick();
      
      try {
        // Use client ID directly as string - simpler and more reliable
        const qrData = String(clientId);
        
        this.qrCodeUrl = await QRCode.toDataURL(qrData, {
          width: 150,
          margin: 1,
          color: {
            dark: '#2c3e50',
            light: '#ffffff'
          },
          errorCorrectionLevel: 'M'
        });
        
      } catch (error) {
        alert('Error generating QR code:', error, error.message, error.stack);
        this.qrCodeUrl = 'ERROR'; // Special value to show error state
      }
    },
    formatDate(date) {
      return helper.formatDate(date);
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString();
    },
    formatDateTime(date) {
      return new Date(date).toLocaleString();
    },
    calculateDaysRemaining() {
      if (!this.subscription || !this.subscription.end_date) return 0;
      const now = new Date();
      const endDate = new Date(this.subscription.end_date);
      const diffTime = endDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },
    calculateDuration(checkIn, checkOut) {
      if (!checkOut) return '-';
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffMs = end - start;
      const diffMins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    },
    async fetchAttendanceRecords() {
      try {
        this.loadingAttendance = true;
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/attendance?subscription_id=${this.subscription.id}&limit=10`
        );
        this.attendanceRecords = response.data.data || [];
      } catch (error) {
        alert('Error fetching attendance records:', error);
        this.attendanceRecords = [];
      } finally {
        this.loadingAttendance = false;
      }
    },
    async fetchHistoryRecords() {
      try {
        this.loadingHistory = true;
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/subscriptions/${this.subscription.id}/history`
        );
        this.historyRecords = response.data || [];
      } catch (error) {
        alert('Error fetching history records:', error);
        this.historyRecords = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    getHistoryTypeClass(type) {
      const classes = {
        'created': 'bg-success',
        'paused': 'bg-warning',
        'resumed': 'bg-info',
        'frozen': 'bg-primary',
        'canceled': 'bg-danger',
        'renewed': 'bg-success'
      };
      return classes[type] || 'bg-secondary';
    },
    async downloadSubscriptionDetails() {
      try {
        const element = this.$refs.captureElement;
        const canvas = await html2canvas(element, {
          scale: 2, // Reduced scale to decrease size
          backgroundColor: null,
          logging: false,
          useCORS: true,
          allowTaint: true,
          removeContainer: true
        });
        
        // Compress the image for download
        const compressedImageBase64 = await this.compressImage(canvas.toDataURL('image/png', 0.8));
        
        const link = document.createElement('a');
        link.href = compressedImageBase64;
        link.download = `membership-card-${this.subscription.client?.name?.replace(/\s+/g, '-').toLowerCase()}-${this.subscription.client?.id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.vueOpener.open({
          message: this.$t('card_downloaded_successfully'),
          type: 'success',
          position: 'bottom'
        });
      } catch (err) {
        window.vueOpener.open({
          message: this.$t('error_downloading_card'),
          type: 'error',
          position: 'bottom'
        });
      }
    },
    toggleEmailInput() {
      this.showEmailInput = !this.showEmailInput;
      this.emailAddress = '';
    },
    async sendEmail() {
      if (!this.emailAddress) {
        window.vueOpener.open({
          message: this.$t('please_enter_email_address'),
          type: 'warning',
          position: 'bottom'
        });
        return;
      }

      this.sendingEmail = true;
      try {
        // Generate the membership card image
        const element = this.$refs.captureElement;
        const canvas = await html2canvas(element, {
          scale: 2, // Reduced scale to decrease size
          backgroundColor: null,
          logging: false,
          useCORS: true,
          allowTaint: true,
          removeContainer: true
        });
        
        // Compress the image to reduce payload size
        const compressedImageBase64 = await this.compressImage(canvas.toDataURL('image/png', 0.7));

        // Send the email via API
        await helper.customPost(`${process.env.VUE_APP_API}/subscriptions/send-card-email`, {
          subscription_id: this.subscription.id,
          email: this.emailAddress,
          card_image_base64: compressedImageBase64
        });

        window.vueOpener.open({
          message: this.$t('email_sent_successfully'),
          type: 'success',
          position: 'bottom'
        });

        // Reset the form
        this.showEmailInput = false;
        this.emailAddress = '';
      } catch (err) {
        window.vueOpener.open({
          message: err.response?.data?.message || this.$t('error_sending_email'),
          type: 'error',
          position: 'bottom'
        });
      } finally {
        this.sendingEmail = false;
      }
    },
    async compressImage(dataUrl, quality = 0.7) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas size (reduce dimensions)
          const maxWidth = 800;
          const maxHeight = 600;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/png', quality);
          resolve(compressedDataUrl);
        };
        img.src = dataUrl;
      });
    }
  },
  mounted() {
    if (this.subscription && this.show) {
      this.generateQRCode();
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block;
  background: rgba(0,0,0,0.5);
}

.subscription-details {
  padding: 0;
  display: flex;
  justify-content: center;
  background: transparent;
  border-radius: 0;
}

.subscription-details.modal-view {
  padding: 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 1rem;
}

.subscription-details.capture-only {
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

.business-card {
  width: 480px;
  height: 320px;
  background: #ffffff;
  border-radius: 1rem;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  border: 1px solid #e9ecef;
}

.card-front {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #303030 0%, #303030 100%);
  position: relative;
}

.card-front::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  pointer-events: none;
}

.card-header {
  padding: 1.5rem 2rem 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.business-logo {
  height: 50px;
  width: auto;
  background: white;
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.company-info {
  flex: 1;
}

.company-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.company-tagline {
  font-size: 0.9rem;
  color: rgba(255,255,255,0.9);
  margin: 0;
  font-weight: 400;
}

.card-body {
  flex: 1;
  padding: 1rem 2rem;
  background: #ffffff;
  position: relative;
  z-index: 1;
}

.client-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.client-info {
  flex: 1;
}

.client-name {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.membership-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 600;
}

.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 1rem;
}

.qr-container {
  background: #ffffff;
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 2px solid #e9ecef;
}

.qr-code {
  width: 100px;
  height: 100px;
  display: block;
}

.qr-loading {
  width: 100px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.qr-loading small {
  font-size: 0.7rem;
  text-align: center;
}

.qr-instruction {
  font-size: 0.7rem;
  color: #6c757d;
  text-align: center;
  margin: 0.5rem 0 0 0;
  max-width: 120px;
  line-height: 1.3;
}

.card-footer {
  padding: 1rem 2rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  position: relative;
  z-index: 1;
}

.contact-info {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.contact-item {
  font-size: 0.75rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.contact-item i {
  font-size: 0.8rem;
  color: #667eea;
}

.btn-primary {
  background: linear-gradient(135deg, #004aad 0%, #004aad 100%);
  border: none;
  padding: 0.75rem 2rem;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
}

.btn-secondary {
  background: #6c757d;
  border: none;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .business-card {
    width: 100%;
    max-width: 400px;
    height: 280px;
  }
  
  .card-header {
    padding: 1rem 1.5rem 0.5rem;
  }
  
  .company-name {
    font-size: 1.2rem;
  }
  
  .client-name {
    font-size: 1.5rem;
  }
  
  .contact-info {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .contact-item {
    font-size: 0.7rem;
  }
}

/* Email Section Styling */
.email-section {
  width: 100%;
  margin-bottom: 1rem;
}

.email-section .input-group {
  max-width: 500px;
}

.email-section .form-control {
  border-radius: 0.375rem 0 0 0.375rem;
  border: 1px solid #ced4da;
}

.email-section .btn-success {
  border-radius: 0 0.375rem 0.375rem 0;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.email-section .btn-success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.email-section .btn-success:disabled {
  opacity: 0.6;
  transform: none;
  box-shadow: none;
}

/* Modal Footer Styling */
.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.modal-footer .btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.875rem;
}

.modal-footer .btn-outline-primary {
  border: 2px solid #cbd5e1;
  color: #334155;
  background: transparent;
}

.modal-footer .btn-outline-primary:hover {
  background: #004aad;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(1, 75, 173, 0.3);
}

.modal-footer .btn-primary {
  background: linear-gradient(135deg, #004aad 0%, #004aad 100%);
  border: none;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.modal-footer .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
}

.modal-footer .btn-secondary {
  background: #6c757d;
  border: none;
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
}

.modal-footer .btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.4);
}

/* Responsive Footer */
@media (max-width: 576px) {
  .modal-footer .d-flex {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .modal-footer .btn {
    width: 100%;
    margin-bottom: 0.5rem;
  }
  
  .email-section .input-group {
    flex-direction: column;
  }
  
  .email-section .form-control {
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
  }
  
  .email-section .btn-success {
    border-radius: 0.375rem;
    width: 100%;
  }
}

/* New Detailed Sections Styling */
.subscription-details-container {
  padding: 0;
}

.info-card {
  background: white;
  border-radius: 0.5rem;
  padding: 0.75rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.info-card-icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  font-size: 1.1rem;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

.info-card-icon.text-success {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.info-card-icon.text-danger {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.info-card-icon.text-warning {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.info-card-icon.text-info {
  background: rgba(13, 202, 240, 0.1);
  color: #0dcaf0;
}

.info-card-icon.text-secondary {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
}

.info-card-content {
  flex: 1;
  min-width: 0;
}

.info-card-title {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
  font-weight: 600;
}

.info-card-value {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.2;
}

.details-section {
  margin-top: 1.5rem;
}

.nav-tabs {
  border-bottom: 2px solid #e9ecef;
}

.nav-tabs .nav-link {
  color: #6c757d;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
  color: #667eea;
  border-bottom-color: #667eea;
}

.nav-tabs .nav-link.active {
  color: #667eea;
  background: transparent;
  border-bottom-color: #667eea;
}

.tab-content {
  padding: 1.5rem;
  background: white;
  border-radius: 0 0 0.75rem 0.75rem;
  border: 1px solid #e9ecef;
  border-top: none;
}

.detail-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f5;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item strong {
  color: #495057;
  font-weight: 600;
  display: inline-block;
  min-width: 150px;
}

.detail-item span,
.detail-item p {
  color: #6c757d;
}

.plan-features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
}

.feature-item i {
  font-size: 1.1rem;
}

.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 0.5rem;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f1f3f5;
}

.timeline-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: -1.625rem;
  top: 0.25rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #667eea;
  border: 2px solid white;
  box-shadow: 0 0 0 2px #e9ecef;
}

.timeline-marker.bg-success {
  background: #28a745;
}

.timeline-marker.bg-warning {
  background: #ffc107;
}

.timeline-marker.bg-danger {
  background: #dc3545;
}

.timeline-marker.bg-info {
  background: #0dcaf0;
}

.timeline-marker.bg-primary {
  background: #667eea;
}

.timeline-content {
  padding-left: 0.5rem;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.timeline-header strong {
  color: #2c3e50;
  font-weight: 600;
}

.timeline-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
}

/* Responsive adjustments for detailed sections */
@media (max-width: 768px) {
  .info-card {
    padding: 0.6rem;
  }
  
  .info-card-icon {
    width: 32px;
    height: 32px;
    min-width: 32px;
    font-size: 1rem;
  }
  
  .info-card-title {
    font-size: 0.7rem;
  }
  
  .info-card-value {
    font-size: 0.9rem;
  }
  
  .nav-tabs .nav-link {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
  
  .tab-content {
    padding: 1rem;
  }
  
  .detail-item strong {
    min-width: 120px;
    font-size: 0.9rem;
  }
  
  .plan-features {
    grid-template-columns: 1fr;
  }
  
  .timeline {
    padding-left: 1.5rem;
  }
}

/* Modal Footer Button Styles */
.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.modal-footer .btn-sm {
  padding: 0.4rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
